import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  message,
  InputNumber,
  Alert,
} from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TransactionService, {
  MaturityScheduleTransactionRequest,
} from '../../service/transaction-service/Transaction.service';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import useEmployee from '../../hook/useEmployee';
import useRole from '../../hook/useRole';

interface DepositModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  maturitySchedule: MaturityScheduleResponse;
}

const DepositModal: React.FC<DepositModalProps> = ({
  visible,
  onClose,
  onSuccess,
  maturitySchedule,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { employee } = useEmployee();
  const role = useRole();

  const handleSubmit = async (values: any) => {
    try {
      if (!employee?.id) {
        message.error('Không tìm thấy thông tin nhân viên');
        return;
      }

      setLoading(true);

      const request: MaturityScheduleTransactionRequest = {
        maturityScheduleId: maturitySchedule.id,
        transactionDate: values.transactionDate.format('YYYY-MM-DD'),
        amount: values.amount,
        transactionType: 'deposit',
        status: 'COMPLETED',
        transactionFor: 'maturity',
      };

      const response =
        await TransactionService.createTransactionForMaturitySchedule(
          request,
          employee.id
        );

      if (response.success) {
        message.success(
          response.message || 'Tạo giao dịch nạp tiền thành công!'
        );
        onSuccess();
        handleClose();
      } else {
        message.error(response.error || 'Có lỗi xảy ra khi tạo giao dịch');
      }
    } catch (error: any) {
      console.error('Error creating deposit transaction:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Có lỗi xảy ra khi tạo giao dịch';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber) return 'N/A';
    const lastFour = cardNumber.slice(-4);
    return `***${lastFour}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const getAccountTypeText = () => {
    return role === 'ROLE_ADMIN' ? 'Tài khoản công ty' : 'Tài khoản cá nhân';
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Đang chờ';
      case 'OVERDUE':
        return 'Quá hạn';
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'PAID':
        return 'Đã nạp đáo';
      default:
        return status;
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-1">
          <ArrowUpOutlined className="text-blue-500 text-sm" />
          <span className="text-sm">Tạo GD nạp tiền</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      {/* Maturity Schedule Info */}
      <div className="bg-gray-50 p-2 rounded-lg mb-3">
        <h4 className="font-semibold mb-1 text-xs">Thông tin kỳ đáo hạn</h4>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div>
            <span className="text-gray-600">Thẻ tín dụng:</span>{' '}
            <span className="font-medium">
              {formatCardNumber(maturitySchedule.creditCard?.cardNumber)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Ngày đáo hạn:</span>{' '}
            <span className="font-medium">
              {dayjs(maturitySchedule.dueDate).format('DD-MM-YYYY')}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Số tiền đáo hạn:</span>{' '}
            <span className="font-medium text-blue-600">
              {formatCurrency(maturitySchedule.repaymentAmount)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Trạng thái:</span>{' '}
            <span className="font-medium">
              {getStatusText(maturitySchedule.status)}
            </span>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          transactionDate: dayjs(), // Mặc định là ngày hôm nay
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          {/* Transaction Date */}
          <Form.Item
            label={<span className="text-xs">Ngày GD</span>}
            name="transactionDate"
            rules={[
              { required: true, message: 'Vui lòng chọn ngày giao dịch' },
            ]}
          >
            <DatePicker
              placeholder="Chọn ngày"
              className="w-full"
              size="small"
              format="DD/MM/YYYY"
              disabledDate={(current) => {
                if (!current) return false;
                const today = dayjs().startOf('day');
                const min = today.subtract(10, 'day');
                const max = today.add(10, 'day');
                return current.isBefore(min, 'day') || current.isAfter(max, 'day');
              }}
            />
          </Form.Item>

          {/* Amount */}
          <Form.Item
            label={<span className="text-xs">Số tiền nạp</span>}
            name="amount"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              {
                type: 'number',
                max: 999999999,
                message: 'Số tiền không được vượt quá 999,999,999 VND',
              },
            ]}
          >
            <InputNumber
              placeholder="Nhập số tiền"
              className="w-full"
              size="small"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              min={0}
              addonAfter="VND"
            />
          </Form.Item>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-2 pt-2 border-t">
          <Button onClick={handleClose} size="small">
            Hủy
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<ArrowUpOutlined />}
            className="bg-blue-500 hover:bg-blue-600"
            size="small"
          >
            Tạo GD nạp tiền
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DepositModal;
